<template>
  <div class="bol-page">
    <div class="infos">
      <div class="features f-r-vc">
        <el-button
          type="primary"
          class="feature-secondary"
          @click="downloadBol"
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px"
          />
          Download BOL
        </el-button>

        <el-button
          type="success"
          class="feature-secondary"
          @click="exportGoodsExcel"
        >
          <svg-icon
            icon-class="info"
            class="svg-white-color"
            style="font-size: 18px"
          />
          Download Excels
        </el-button>
      </div>
      <div class="features f-c">
        <div>Adjust Goods Section Font Size</div>
        <el-input-number
          class="goods-per-page-input"
          v-model="bolGoodsFontSize"
          placeholder="Goods per page"
          :min="1"
          @change="updateBolGoodsFontSize"
        />
        <div>Adjust Goods Count Per Page</div>
        <el-input-number
          class="goods-per-page-input"
          v-model="goodsCntPerPage"
          placeholder="Goods per page"
          :min="1"
          @change="updateGoodsCntPerPage"
        />
      </div>
      <shipment-info
        class="info"
        :shipment-no="shipmentNo"
        :shipment="shipment"
        :mode="FormModes.SINGLE"
        :editable="false"
      />
      <truck-info
        :shipment-no="shipmentNo"
        :shipment="shipment"
        :mode="FormModes.SINGLE"
        :editable="false"
      />
      <third-party-bill-info
        :third-party-bill-info="thirdPartyBillInfo"
        :mode="FormModes.SINGLE"
        :shipment-id="shipmentId"
        :editable="false"
        @input="updateThirdPartyBillInfo"
        @resetThirdPartyBillInfo="resetThirdPartyBillInfo"
      />
    </div>
    <div class="bol-preview-container" v-if="shipment">
      <div
        class="bol-page-group"
        v-for="(groupName, groupIndex) in Object.keys(goodsGroups)"
        :key="'bol-page-group-' + groupIndex"
      >
        <h1>
          {{
            addressIdToCode[groupName]
              ? addressIdToCode[groupName]
              : bolInfoList[groupIndex]?.address
          }}
        </h1>
        <common-card
          class="bol-page-container"
          v-for="index in pageCounts[groupName]"
          :key="'bol-page-group-' + groupIndex + '-bol-page-' + index"
        >
          <bol-template
            :shipment-no="shipmentNo"
            :shipment-id="shipmentId"
            :truck-info="truckInfo"
            :third-party-bill-info="thirdPartyBillInfo"
            :group-name="groupName"
            :goods-ids="
              goodsGroups[groupName].slice(
                (index - 1) * goodsCntPerPage,
                Math.min(index * goodsCntPerPage, goodsGroups[groupName].length)
              )
            "
            :pallet-cnt="groupPalletCnts[groupName]"
            :bol-info="bolInfoList[groupIndex] || {}"
            :show-add-fba-button="showAddFbaButton"
            :total-page-count="
              Math.ceil(goodsGroups[groupName].length / goodsCntPerPage)
            "
            :page-index="index"
            :goods-font-size="bolGoodsFontSize"
          />
        </common-card>
      </div>
    </div>
  </div>
</template>

<script>
// components
import BolTemplate from '@/components/shipment/BolTemplate.vue'
import CommonCard from '@/components/common/CommonCard.vue'
import ShipmentInfo from '@/components/shipment/ShipmentInfo.vue'
import TruckInfo from '@/components/shipment/PickUpInfo.vue'
import ThirdPartyBillInfo from '@/components/shipment/ThirdPartyBillInfo.vue'
// models
import { FormModes } from '@/core/configs/types'
import { BolGoodsCntPerPage } from '@/core/models/shipment'
// apis
import { detailShipment } from '@/api/shipment'
import { queryPallets, detailPallet } from '@/api/pallet'
import { queryGoods } from '@/api/goods'
import { queryAddress } from '@/api/address'
// utils
import { downloadPdf } from '@/core/utils/pdf'
import { exportExcel } from '@/core/utils/excel'
// msg
import PagesMessage from '@/core/message/pages'

export default {
  name: 'BolPage',
  components: {
    BolTemplate,
    CommonCard,
    ShipmentInfo,
    TruckInfo,
    ThirdPartyBillInfo
  },
  data() {
    return {
      FormModes,
      shipment: null,
      truckInfo: {},
      originalThirdPartyBillInfo: {},
      thirdPartyBillInfo: {},
      originalOtherInfo: {},
      otherInfo: {},
      goodsGroups: {},
      goodsCntPerPage: BolGoodsCntPerPage,
      groupPalletCnts: {},
      bolInfoList: [],
      showAddFbaButton: true,
      addressIdToCode: null,
      bolGoodsFontSize: 12
    }
  },
  computed: {
    shipmentNo() {
      return this.$route.params.no
    },
    shipmentId() {
      return this.shipment?.id ?? ''
    },
    pageCounts() {
      const pageCounts = {}
      for (const groupName of Object.keys(this.goodsGroups)) {
        pageCounts[groupName] = Math.ceil(this.goodsGroups[groupName].length / this.goodsCntPerPage)
      }
      return pageCounts
    }
  },
  methods: {
    async getShipmentDetail() {
      const shipment = await detailShipment(this.shipmentNo)
      this.$set(this, 'shipment', shipment)
      this.$set(this, 'thirdPartyBillInfo', shipment?.billTo ?? {})
      this.$set(this, 'truckInfo', {
        trailerNo: shipment.trailerNo,
        sealNo: shipment.sealNo
      })
      this.$set(this.truckInfo, 'sealNo', shipment?.sealNo ?? '')
      const palletIds = this.shipment.palletIds
      const palletList = (await queryPallets({ ids: palletIds, size: -1 }))
        .records
      const goodsGroups = {}
      const groupPalletCnts = {}
      const orderGroupSet = new Set()

      const addressIds = [...new Set(palletList.map(p => p.addressId))]
      const addressList = (await queryAddress({ addressIds, size: -1 })).records
      const addressIdToCode = Object.fromEntries(
        addressList.map(address => [
          address.id,
          [address.name, address.code].join(' ')
        ])
      )
      this.$set(this, 'addressIdToCode', addressIdToCode)
      for (const pallet of palletList) {
        // address id as groupName
        const groupName = pallet.addressId
        const orderId = pallet.orderId
        if (!(groupName in goodsGroups)) goodsGroups[groupName] = []
        const jobGroupName = pallet.groupName
        // pallets in same job group have save goods, avoid request goods repeadly
        // empty jobGroupName means the pallet is from local order
        if (
          (jobGroupName &&
            !orderGroupSet.has([orderId, jobGroupName].join())) ||
          (!jobGroupName && !orderGroupSet.has([orderId, groupName].join()))
        ) {
          if (jobGroupName) {
            orderGroupSet.add([orderId, jobGroupName].join())
          } else {
            orderGroupSet.add([orderId, groupName].join())
          }
          const palletDetail = await detailPallet(pallet.no)
          const goodsIds = palletDetail.goodsIds
          goodsGroups[groupName] = [
            ...new Set([...goodsGroups[groupName], ...(goodsIds ?? [])])
          ]
        }
        if (!(groupName in groupPalletCnts)) groupPalletCnts[groupName] = 0
        groupPalletCnts[groupName]++
      }
      this.$set(this, 'goodsGroups', goodsGroups)
      this.$set(this, 'groupPalletCnts', groupPalletCnts)
      this.$set(this.otherInfo, 'groupNameList', Object.keys(goodsGroups))
      const curBols = this.shipment?.bols ?? []
      const bolInfoList = []
      for (const group of Object.keys(goodsGroups)) {
        const index = curBols.findIndex(one => one.name === group)
        let bol
        if (index === -1) {
          const addrestList = (
            await queryAddress({ addressIds: [group], size: 1 })
          ).records
          if (addrestList.length === 0) {
            bol = {
              name: addressIdToCode[group] ? addressIdToCode[group] : '',
              address: '',
              city: '',
              state: '',
              country: '',
              zip: '',
              instruction: ''
            }
          } else {
            const addressRecord = addrestList[0]
            bol = {
              name: addressIdToCode[group] ? addressIdToCode[group] : '',
              address: addressRecord.street,
              city: addressRecord.city,
              state: addressRecord.state,
              country: addressRecord.country,
              zip: addressRecord.zip,
              instruction: ''
            }
          }
        } else {
          bol = curBols[index]
        }
        bolInfoList.push(bol)
      }
      this.$set(this, 'bolInfoList', bolInfoList)
    },
    updateThirdPartyBillInfo(e) {
      this.$set(this, 'thirdPartyBillInfo', {
        ...this.thirdPartyBillInfo,
        ...e
      })
    },
    resetThirdPartyBillInfo() {
      this.$set(this, 'thirdPartyBillInfo', this.originalThirdPartyBillInfo)
    },
    async downloadBol() {
      this.$set(this, 'showAddFbaButton', false)
      setTimeout(() => {
        this.getPdf('.bol-template').then(pdf => {
          downloadPdf(pdf, this.shipmentNo + '-bol')
          this.$set(this, 'showAddFbaButton', true)
        })
      }, 1000)
    },
    async exportGoodsExcel() {
      const excelInfo = []
      const columns = ['PRO', 'PO List (use , as separator) *']
      for (const groupName of Object.keys(this.goodsGroups)) {
        const info = {}
        info.sheetName = groupName
        info.columns = columns
        const rows = []
        const goodsIds = this.goodsGroups[groupName]
        await queryGoods({ ids: goodsIds, size: -1 }).then(res => {
          const goodsList = res.records
          goodsList.forEach(goods => {
            const row = {
              PRO: goods.sku,
              'PO List (use , as separator) *': goods.referenceNo
            }
            rows.push(row)
          })
        })
        info.rows = rows
        excelInfo.push(info)
      }
      exportExcel(excelInfo)
    },
    updateBolGoodsFontSize(value) {
      this.$set(this, 'bolGoodsFontSize', value)
    },
    updateGoodsCntPerPage(value) {
      this.goodsCntPerPage = value
      const saved = this.shipment
      this.$set(this, 'shipment', null)
      setTimeout(() => {
        this.$set(this, 'shipment', saved)
      }, 1000)
    }
  },
  mounted() {
    this.getShipmentDetail()
    PagesMessage.on(
      PagesMessage.eventMap.REFRESH_SHIPMENT_BOL,
      this.getShipmentDetail
    )
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/styles/common.scss';

.bol-page {
  @extend .f-r;

  .infos {
    min-width: 500px !important;

    @extend .f-c;
  }

  .common-card {
    @extend .f-c-hc;
  }

  .bol-preview-container {
    flex-shrink: 0;

    ::v-deep .bol-page-container {
      margin-left: 0 !important;
    }
  }
}

.features {
  // margin: 10px 0 10px 10px;
  margin-left: 20px;
  margin-top: 5px;
  margin-bottom: 0px;
}

.goods-per-page-input {
  width: 360px;
}
</style>
